import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import axiosConfig from "../axiosConfig";
import Header from "../Header/Header";
import MainMenu from "../MainMenu/MainMenu";

const normalizeDeviceOption = (device) => {
  if (!device || device.id === undefined || device.id === null) {
    return null;
  }
  return {
    value: String(device.id),
    label: String(device.id),
  };
};

const sanitizeCellValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).trim();
};

const parseHtmlRows = (html) => {
  if (typeof DOMParser === "undefined") {
    return [];
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const rawRows = Array.from(doc.querySelectorAll("tr"));
  if (rawRows.length <= 1) {
    return [];
  }
  return rawRows.slice(1).map((tr) => Array.from(tr.children).map((td) => sanitizeCellValue(td.textContent)));
};

const exportCsv = (headers, rows, filename) => {
  if (typeof document === "undefined") {
    return;
  }
  const sanitizeForCsv = (value) => {
    const safe = (value ?? "").replace(/"/g, '""');
    return /[",\n]/.test(safe) ? `"${safe}"` : safe;
  };
  const escapedRows = rows.map((row) => row.map((cell) => sanitizeForCsv(cell)));
  const escapedHeaders = headers.map((header) => sanitizeForCsv(header));
  const payload = [escapedHeaders, ...escapedRows].map((line) => line.join(",")).join("\n");
  const blob = new Blob([payload], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const DEVICE_BOX_PRIMARY_ENDPOINT = "/device-check/device-box-ids";
const DEVICE_BOX_FALLBACK_ENDPOINT = "/device-check/available-boxes";

const RawRequestViewer = ({ title, endpointBuilder, menuKey, columns, helperText }) => {
  const [deviceId, setDeviceId] = useState("");
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [deviceError, setDeviceError] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  useEffect(() => {
    const fetchDeviceIds = async () => {
      try {
        const { data } = await axiosConfig.get(DEVICE_BOX_PRIMARY_ENDPOINT);
        const normalized = (data || [])
          .map((device) => normalizeDeviceOption(device))
          .filter(Boolean)
          .sort((a, b) => Number(a.value) - Number(b.value));
        setDeviceOptions(normalized);
        setDeviceError("");
      } catch (primaryError) {
        try {
          const { data: fallbackData } = await axiosConfig.get(DEVICE_BOX_FALLBACK_ENDPOINT);
          const normalizedFallback = (fallbackData || [])
            .map((device) => normalizeDeviceOption(device))
            .filter(Boolean)
            .sort((a, b) => Number(a.value) - Number(b.value));
          setDeviceOptions(normalizedFallback);
          setDeviceError("");
        } catch (fallbackError) {
          const serverMessage =
            fallbackError?.response?.data?.error ||
            (typeof fallbackError?.response?.data === "string" && fallbackError.response.data) ||
            fallbackError?.message ||
            "Please refresh the page.";
          setDeviceError(`Unable to load device IDs. ${serverMessage}`.trim());
        }
      }
    };

    fetchDeviceIds();
  }, []);

  const rowsFromObjects = (records) => {
    if (!Array.isArray(records)) {
      return [];
    }
    return records.map((record) => {
      const rowObj = {};
      columns.forEach((column) => {
        rowObj[column.key] = sanitizeCellValue(record?.[column.key]);
      });
      return rowObj;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!deviceId) {
      setFetchError("Select a device ID before fetching data.");
      return;
    }
    setLoading(true);
    setFetchError("");
    try {
      const endpoint = endpointBuilder(deviceId);
      if (!endpoint) {
        throw new Error("Endpoint is not configured for this view.");
      }
      const client = typeof endpoint === "string" && endpoint.startsWith("http") ? axios : axiosConfig;
      const response = await client.get(endpoint);

      let normalizedRows = [];
      if (Array.isArray(response?.data?.data)) {
        normalizedRows = rowsFromObjects(response.data.data);
      } else if (Array.isArray(response?.data)) {
        normalizedRows = rowsFromObjects(response.data);
      } else if (typeof response?.data === "string") {
        const parsedRows = parseHtmlRows(response.data);
        normalizedRows = parsedRows.map((cells) => {
          const rowObj = {};
          columns.forEach((column, index) => {
            rowObj[column.key] = sanitizeCellValue(cells[index] ?? "");
          });
          return rowObj;
        });
      } else if (response?.data?.records) {
        normalizedRows = rowsFromObjects(response.data.records);
      }

      setRows(normalizedRows);
      setLastFetchedAt(new Date());
      if (!normalizedRows.length) {
        const emptyMessage = "No raw packets found for this device box.";
        setFetchError(emptyMessage);
      }
    } catch (error) {
      const serverMessage =
        (typeof error?.response?.data === "string" && error.response.data) ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Please try again.";
      const composedMessage = `Unable to fetch raw request data. ${serverMessage}`.trim();
      setFetchError(composedMessage);
      setRows([]);
      setLastFetchedAt(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!rows.length) {
      return;
    }
    const headers = columns.map((column) => column.label);
    const payload = rows.map((row) => columns.map((column) => row[column.key] ?? ""));
    const filename = `${title.replace(/\s+/g, "_")}_${new Date().toISOString()}.csv`;
    exportCsv(headers, payload, filename);
  };

  return (
    <div>
      <Header title={title} />
      <MainMenu menu={menuKey} />
      <div className="app-content content" style={{ backgroundColor: "azure", minHeight: "36em" }}>
        <div className="content-overlay"></div>
        <div className="content-wrapper">
          <div className="content-header row"></div>
          <div className="content-body">
            <div className="row justify-content-md-center">
              <div className="col-xl-12 col-12">
                <section id="raw-requests">
                  <div className="row">
                    <div className="col-12">
                      <div className="card">
                        <div className="card-content collapse show">
                          <div className="card-body card-dashboard">
                            <div className="mb-2">
                              <h2 style={{ textAlign: "center" }}>{title}</h2>
                              {helperText && <p className="text-muted text-center">{helperText}</p>}
                              {deviceError && (
                                <p className="text-danger text-center mb-1">{deviceError}</p>
                              )}
                              <form onSubmit={handleSubmit} className="mt-2">
                                <div className="form-row align-items-end">
                                  <div className="col-md-6 mb-1">
                                    <label htmlFor="deviceSelect"><b>Select Device Box ID</b></label>
                                    <div className="position-relative" style={{ zIndex: 1050 }}>
                                      <Select
                                        inputId="deviceSelect"
                                        classNamePrefix="device-select"
                                        options={deviceOptions}
                                        value={deviceOptions.find((option) => option.value === deviceId) || null}
                                        onChange={(option) => setDeviceId(option?.value || "")}
                                        placeholder="Type to search box ID"
                                        isClearable
                                        isSearchable
                                      />
                                    </div>
                                  </div>
                                  <div className="col-md-3 mb-1">
                                    <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                                      {loading ? "Loading..." : "Fetch"}
                                    </button>
                                  </div>
                                  <div className="col-md-3 mb-1">
                                    <button
                                      type="button"
                                      className="btn btn-success btn-block"
                                      onClick={handleExport}
                                      disabled={!rows.length}
                                    >
                                      Export CSV
                                    </button>
                                  </div>
                                </div>
                              </form>
                              {fetchError && <p className="text-danger mt-1">{fetchError}</p>}
                              {lastFetchedAt && (
                                <p className="text-muted mt-1">
                                  Showing {rows.length} records fetched at {lastFetchedAt.toLocaleString()}
                                </p>
                              )}
                            </div>
                            <div className="table-responsive">
                              <table className="table table-striped table-bordered">
                                <thead className="sticky-top" style={{ backgroundColor: "#4cbfff" }}>
                                  <tr>
                                    {columns.map((column) => (
                                      <th key={column.key}>{column.label}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {loading ? (
                                    <tr>
                                      <td colSpan={columns.length} style={{ textAlign: "center" }}>
                                        Fetching data...
                                      </td>
                                    </tr>
                                  ) : rows.length ? (
                                    rows.map((row, index) => (
                                      <tr key={`${row.device_id || "row"}-${index}`}>
                                        {columns.map((column) => (
                                          <td key={`${column.key}-${index}`}>{row[column.key] || "-"}</td>
                                        ))}
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan={columns.length} style={{ textAlign: "center" }}>
                                        No data available. Select a device box and fetch raw requests.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RawRequestViewer;
